// Copyright (c) StrangeBee 2023

import { ListItemsPerPageType } from '@Constants/pagination';
import produce, { setAutoFreeze } from 'immer';
import { FilterInput } from 'thehive-sdk';
import useLocalStorage, { LocalStorageKeys } from './useLocalStorage';

setAutoFreeze(false);

export enum StorageFilterEnum {
    ALERT = 'alert',
    ALERT_HISTORY = 'alertHistory',
    ALERT_OBSERVABLE = 'alertObservable',
    ALERT_PROCEDURE = 'alertProcedure',
    ALERT_RELATED = 'alertRelated',
    ALERT_SIMILAR_ALERTS = 'alertSimilarAlerts',
    ALERT_SIMILAR_CASES = 'alertSimilarCases',
    ALERT_STATUS = 'alertStatus',
    ANALYZER_TEMPLATE = 'analyzerTemplate',
    ATTACHMENTS = 'attachments',
    ATTACK_PATTERNS = 'pattern',
    CASE = 'case',
    CASE_ATTACHMENTS = 'caseAttachments',
    CASE_OBSERVABLE = 'caseObservable',
    CASE_PROCEDURE = 'caseProcedure',
    CASE_RELATED = 'caseRelated',
    CASE_RESPONDER = 'caseResponder',
    CASE_SIMILAR_ALERTS = 'caseSimilarAlerts',
    CASE_STATUS = 'caseStatus',
    CASE_TEMPLATE = 'caseTemplate',
    CASE_HISTORY = 'caseHistory',
    CATALOG_ATTACK_PATTERNS = 'catalogPattern',
    CUSTOM_FIELD = 'customField',
    CUSTOM_TAGS = 'customTags',
    DASHBOARD = 'dashboard',
    FUNCTION = 'function',
    GLOBAL_TASKS = 'globalTasks',
    GLOBAL_USERS = 'globalUsers',
    LINKED_ORGANISATIONS = 'linkedOrganisations',
    MY_TASKS = 'myTasks',
    OBSERVABLE_TYPE = 'observableType',
    ORGANISATION = 'organisation',
    ORGANISATION_DRAWER_USERS = 'organisationDrawerUsers',
    PAGE_TEMPLATE = 'pageTemplate',
    PROFILE = 'profile',
    REPORT_TEMPLATE = 'reportTemplate',
    TASKS = 'tasks',
    TAXONOMY = 'taxonomy',
    USERS = 'users',
    WAITING_TASKS = 'waitingTasks',
}

export interface StoragePageContent {
    filters: FilterInput[];
    itemsPerPage: ListItemsPerPageType;
    statsIsOpen: boolean;
    filterIsOpen: boolean;
    display: Record<string, boolean>;
    sortList: string[];
    listAsGroup: boolean;
    autoRefresh: boolean;
    currentView: string;
}

export const defaultStoragePageContent: StoragePageContent = {
    filters: [],
    itemsPerPage: 30,
    statsIsOpen: false,
    filterIsOpen: false,
    display: {},
    sortList: [],
    listAsGroup: false,
    autoRefresh: true,
    currentView: 'default',
};

export interface UseStoragePageReturn {
    page: StoragePageContent;
    setCurrentView: (newView: string) => void;
    setFilters: (filters: FilterInput[]) => void;
    setFilterIsOpen: (statsIsOpen: boolean) => void;
    setDisplay: (attr: string, value: boolean) => void;
    setItemsPerPage: (itemsPerPage: ListItemsPerPageType) => void;
    setStatsIsOpen: (statsIsOpen: boolean) => void;
    setSort: (sortList: string[]) => void;
    setListAsGroup: (listAsGroup: boolean) => void;
    setAutoRefresh: (autoRefresh: boolean) => void;
    loadView: (viewName: string, viewContent: any) => void;
    setOnLoadView: (fn: (viewContent?: StoragePageContent, viewName?: string) => void) => void;
}

interface UseStoragePageParams {
    organisation: string;
    fieldScope?: StorageFilterEnum;
}

type SetStorageType = React.SetStateAction<{
    [organisation: string]: {
        [fieldScope: string]: StoragePageContent;
    };
} | null>;

export const useStoragePage = ({ organisation, fieldScope }: UseStoragePageParams): UseStoragePageReturn | null => {
    let onLoadView = (viewContent?: StoragePageContent, viewName?: string): void => {
        viewName;
        viewContent;
        return;
    };
    const { storage, setStorage } = useLocalStorage<{
        [organisation: string]: { [fieldScope: string]: StoragePageContent };
    } | null>({
        key: LocalStorageKeys.PAGE,
        initialValue: (value) => {
            if (!fieldScope) {
                return null;
            }

            if (value && value[organisation] && value[organisation][fieldScope]) {
                return value;
            }
            return {
                ...value,
                [organisation]: {
                    ...value?.[organisation],
                    [fieldScope]: {
                        ...defaultStoragePageContent,
                    },
                },
            };
        },
    });

    if (!fieldScope || !storage) return null;

    const setCurrentView = (newView: string): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].currentView = newView;
            }),
        );

    const setFilters = (filters: FilterInput[]): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].filters = filters;
            }),
        );

    const setItemsPerPage = (itemsPerPage: ListItemsPerPageType): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].itemsPerPage = itemsPerPage;
            }),
        );

    const setStatsIsOpen = (statsIsOpen: boolean): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].statsIsOpen = statsIsOpen;
            }),
        );

    const setFilterIsOpen = (filterIsOpen: boolean): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].filterIsOpen = filterIsOpen;
            }),
        );

    const setSort = (sortList: string[]): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].sortList = sortList;
            }),
        );

    const setDisplay = (attr: string, value: boolean): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft === null) return;
                if (!draft[organisation][fieldScope].display) {
                    draft[organisation][fieldScope].display = {};
                }
                draft[organisation][fieldScope].display[attr] = value;
            }),
        );

    const setListAsGroup = (listAsGroup: boolean): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].listAsGroup = listAsGroup;
            }),
        );

    const setAutoRefresh = (autoRefresh: boolean): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) draft[organisation][fieldScope].autoRefresh = autoRefresh;
            }),
        );

    const loadView = (viewName: string, viewContent: any): void =>
        setStorage(
            produce<SetStorageType>((draft) => {
                if (draft) {
                    onLoadView(viewContent, viewName);
                }
                if (draft) draft[organisation][fieldScope] = viewContent;
                if (draft) draft[organisation][fieldScope].currentView = viewName;
            }),
        );
    const setOnLoadView = (fn: (viewContent?: StoragePageContent, viewName?: string) => void) => {
        onLoadView = fn;
    };

    return {
        page: storage[organisation][fieldScope],
        setCurrentView,
        setFilters,
        setFilterIsOpen,
        setDisplay,
        setItemsPerPage,
        setSort,
        setStatsIsOpen,
        setListAsGroup,
        setAutoRefresh,
        loadView,
        setOnLoadView,
    };
};
